import { ClockCounterClockwise, MagnifyingGlass, Sparkle, X } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  SEARCH_DATE_FILTERS,
  SEARCH_STATUSES,
  type SearchDateFilter,
  type SearchParams,
  type SearchStatus,
} from "~/api/libraryTypes";
import { NAVIGATE_AUTO_CONFIDENCE, type NavigateTarget } from "~/api/opsTypes";
import {
  useCollections,
  useForgetSearch,
  useMembers,
  useRecentSearches,
  useRouteIntent,
  useSearchResults,
} from "~/api/queries";
import { DocumentList } from "~/features/documents/DocumentList";
import { isCommandQuery } from "~/features/search/prompt";
import { Select } from "~/features/settings/controls";
import { useThinkerDock } from "~/features/work/piles/pilesApi";
import { Button } from "~/foundation/components/Button";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { cn } from "~/foundation/utils/cn";
import { kindLabel } from "~/shell/askbar/copy";
import { useOpenTarget } from "~/shell/askbar/openTarget";

/** The URL's shape. Every key optional, so `/search` alone is a valid address. */
export interface SearchRouteParams {
  query?: string;
  /** `ask` = the ✨ toggle is on: the box is a prompt, not a term (S2). */
  mode?: "ask";
  collection?: string;
  by?: string;
  date?: SearchDateFilter;
  status?: SearchStatus[];
}

const DATE_LABELS: Record<SearchDateFilter, (t: (key: string) => string) => string> = {
  day: (t) => t("Past day"),
  week: (t) => t("Past week"),
  month: (t) => t("Past month"),
  year: (t) => t("Past year"),
};

const STATUS_LABELS: Record<SearchStatus, (t: (key: string) => string) => string> = {
  published: (t) => t("Published"),
  draft: (t) => t("Drafts"),
  archived: (t) => t("Archived"),
};

/**
 * W10 §6 — search, with the filters the platform really supports.
 *
 * Three honesty constraints shape this scene, all of them from the endpoint:
 *
 * 1. **A filter is not a search.** `documents.search` short-circuits a blank query to an empty
 *    result *before* applying any filter, so a filtered search with no term cannot work. The
 *    scene says so instead of showing an empty result list that looks like "nothing matched".
 * 2. **`userId` is a COLLABORATOR filter** (`CollaboratorIds.Contains`), not an author filter —
 *    so the control is labelled Collaborator. Calling it Author would be a lie about which
 *    documents come back.
 * 3. **Recent searches are a by-product**, written on every request with `offset === 0`. There
 *    is no "save this search" verb, so none is offered — only "forget".
 *
 * The URL is the state: every filter round-trips through `validateSearch`, so a filtered search
 * is shareable and survives a reload.
 *
 * **Search as a prompt (S2).** The same box is also a place to say what to open: "open the
 * research about math". A query that reads as a command (`isCommandQuery`), or the ✨ toggle
 * (`mode=ask` in the address), sends the text to the intent router with `surface: "search"`
 * instead of running the full-text search. A confident `navigate` goes straight there; an
 * ambiguous one becomes a plain "Did you mean…" list where Enter opens the first; anything that
 * is not a navigate hands the prompt to the Ask bar, which owns the confirm cards. A plain term
 * still runs the full-text search exactly as before. Every candidate is labelled with its kind
 * (`kindLabel`) — a document, research, a collection or a work item (S4).
 */
export function SearchScene({ params }: { params: SearchRouteParams }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openTarget = useOpenTarget();
  const openAskBar = useThinkerDock((state) => state.show);
  const { data: collections } = useCollections();
  const { data: members } = useMembers();
  const forget = useForgetSearch();
  const routeIntent = useRouteIntent();

  const query = params.query ?? "";
  const statuses = params.status ?? [];
  const askMode = params.mode === "ask";
  const promptMode = askMode || isCommandQuery(query);

  /** The router's answer when it was not sure enough to just go: a list, or one sentence. */
  const [candidates, setCandidates] = useState<NavigateTarget[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const searchParams: SearchParams = {
    // In prompt mode the full-text search is not run: the endpoint short-circuits a blank term.
    query: promptMode ? "" : query,
    ...(params.collection ? { collectionId: params.collection } : {}),
    ...(params.by ? { userId: params.by } : {}),
    ...(params.date ? { dateFilter: params.date } : {}),
    ...(statuses.length ? { statusFilter: statuses } : {}),
  };

  const { data: results, isFetching } = useSearchResults(searchParams);
  const { data: recent } = useRecentSearches();
  useDocumentTitle(t("Search"));

  /** One writer for the whole address — a filter change never drops the others. */
  const setParams = (next: Partial<SearchRouteParams>) => {
    const merged: SearchRouteParams = { ...params, ...next };
    void navigate({
      to: "/search",
      search: {
        ...(merged.query ? { query: merged.query } : {}),
        ...(merged.mode === "ask" ? { mode: "ask" } : {}),
        ...(merged.collection ? { collection: merged.collection } : {}),
        ...(merged.by ? { by: merged.by } : {}),
        ...(merged.date ? { date: merged.date } : {}),
        ...(merged.status?.length ? { status: merged.status } : {}),
      },
      replace: true,
    });
  };

  const toggleStatus = (status: SearchStatus) => {
    const next = statuses.includes(status)
      ? statuses.filter((value) => value !== status)
      : [...statuses, status];
    setParams({ status: next });
  };

  const submitPrompt = () => {
    const text = query.trim();
    if (!text || routeIntent.isPending) {
      return;
    }
    setCandidates(null);
    setNotice(null);
    routeIntent.mutate(
      { prompt: text, surface: "search" },
      {
        onSuccess: (result) => {
          if (result.actionId === "navigate") {
            if (result.document && result.confidence >= NAVIGATE_AUTO_CONFIDENCE) {
              openTarget(result.document);
              return;
            }
            const found = [result.document, ...result.alternates].filter(
              (target): target is NavigateTarget => target !== null,
            );
            if (found.length === 0) {
              setNotice(t("I couldn't find anything matching that."));
              return;
            }
            setCandidates(found);
            return;
          }
          if (!result.actionId) {
            setNotice(result.message ?? t("I can't do that from here yet."));
            return;
          }
          // A digest, a new document, a notification… — their confirm cards live in the Ask bar.
          openAskBar(text);
        },
        onError: () => {
          setNotice(t("Couldn't understand that. Try naming what to open."));
        },
      },
    );
  };

  const filtered =
    Boolean(params.collection) || Boolean(params.by) || Boolean(params.date) || statuses.length > 0;

  const documents = (results?.hits ?? []).map((hit) => hit.document);

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <Text as="h1" variant="h2">
        {t("Search")}
      </Text>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Input
              type="search"
              value={query}
              autoFocus
              fullWidth
              aria-label={t("Search")}
              placeholder={promptMode ? t("What should I open?") : t("Search…")}
              onChange={(event) => {
                setCandidates(null);
                setNotice(null);
                setParams({ query: event.target.value });
              }}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || !promptMode) {
                  return;
                }
                event.preventDefault();
                if (candidates && candidates.length > 0) {
                  openTarget(candidates[0] as NavigateTarget);
                  return;
                }
                submitPrompt();
              }}
            />
          </div>
          <IconButton
            icon={Sparkle}
            aria-label={t("Ask")}
            aria-pressed={askMode}
            active={promptMode}
            title={t("Ask — tell me what to open")}
            onClick={() => setParams({ mode: askMode ? undefined : "ask" })}
          />
        </div>
        {promptMode ? (
          <Text variant="small">
            {t("Tell me what to open, e.g. open the research about math")}
          </Text>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <Select
          label={t("Collection")}
          value={params.collection ?? ""}
          onChange={(event) => setParams({ collection: event.target.value || undefined })}
        >
          <option value="">{t("Any collection")}</option>
          {collections?.map((collection) => (
            <option key={collection.id} value={collection.id}>
              {collection.name}
            </option>
          ))}
        </Select>

        {/* Labelled Collaborator, not Author: the server matches `CollaboratorIds`. */}
        <Select
          label={t("Collaborator")}
          value={params.by ?? ""}
          onChange={(event) => setParams({ by: event.target.value || undefined })}
        >
          <option value="">{t("Anyone")}</option>
          {members?.map((member) => (
            <option key={member.id} value={member.id}>
              {member.name}
            </option>
          ))}
        </Select>

        <Select
          label={t("Updated")}
          value={params.date ?? ""}
          onChange={(event) =>
            setParams({ date: (event.target.value || undefined) as SearchDateFilter | undefined })
          }
        >
          <option value="">{t("Any time")}</option>
          {SEARCH_DATE_FILTERS.map((filter) => (
            <option key={filter} value={filter}>
              {DATE_LABELS[filter](t)}
            </option>
          ))}
        </Select>

        <div className="flex flex-col gap-1.5">
          <span className="text-small font-medium text-text-secondary">{t("Status")}</span>
          <div className="flex h-8 items-center gap-1">
            {SEARCH_STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={statuses.includes(status)}
                onClick={() => toggleStatus(status)}
                className={cn(
                  "h-8 cursor-pointer rounded-full border px-3 text-small",
                  statuses.includes(status)
                    ? "border-accent bg-accent text-accent-text"
                    : "border-divider bg-bg text-text-secondary hover:border-accent hover:text-text",
                )}
              >
                {STATUS_LABELS[status](t)}
              </button>
            ))}
          </div>
        </div>

        {filtered ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setParams({
                collection: undefined,
                by: undefined,
                date: undefined,
                status: [],
              })
            }
          >
            {t("Clear filters")}
          </Button>
        ) : null}
      </div>

      {promptMode ? (
        <div className="flex flex-col gap-3">
          {routeIntent.isPending ? <Text variant="secondary">{t("Thinking…")}</Text> : null}
          {notice ? <Text variant="secondary">{notice}</Text> : null}
          {candidates && candidates.length > 0 ? (
            <section aria-label={t("Did you mean…")} className="flex flex-col gap-1.5">
              <Text variant="small">{t("Did you mean…")}</Text>
              <ul className="flex flex-col rounded-md border border-divider">
                {candidates.map((candidate, index) => (
                  <li
                    key={`${candidate.kind}:${candidate.id}`}
                    className={cn(
                      "border-b border-divider last:border-b-0",
                      index === 0 && "bg-hover-bg",
                    )}
                  >
                    <button
                      type="button"
                      aria-current={index === 0 ? "true" : undefined}
                      onClick={() => openTarget(candidate)}
                      className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start hover:text-accent"
                    >
                      <span className="min-w-0 flex-1 truncate text-body text-text">
                        {candidate.title}
                      </span>
                      <span className="shrink-0 rounded-full border border-divider px-2 text-small text-text-secondary">
                        {kindLabel(candidate.kind, t)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <Text variant="small">{t("Enter opens the first one.")}</Text>
            </section>
          ) : null}
          {!routeIntent.isPending && !notice && !candidates ? (
            <Text variant="secondary">
              {query.trim() === ""
                ? t("Type what to open and press Enter")
                : t("Press Enter to open it")}
            </Text>
          ) : null}
        </div>
      ) : query.trim() === "" ? (
        <div className="flex flex-col gap-3">
          <Text variant="secondary">
            {filtered
              ? t("Filters need a search term — the platform searches text first, then filters.")
              : t("Type to search this workspace")}
          </Text>

          {recent && recent.length > 0 ? (
            <section aria-label={t("Recent searches")} className="flex flex-col gap-1.5">
              <Text variant="small" className="uppercase tracking-wide">
                {t("Recent searches")}
              </Text>
              <ul className="flex flex-col rounded-md border border-divider">
                {recent.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center gap-2 border-b border-divider px-3 py-2 last:border-b-0"
                  >
                    <ClockCounterClockwise size={16} aria-hidden className="text-text-tertiary" />
                    <button
                      type="button"
                      onClick={() => setParams({ query: entry.query })}
                      className="min-w-0 flex-1 cursor-pointer truncate text-start text-body text-text hover:text-accent"
                    >
                      {entry.query}
                    </button>
                    <IconButton
                      icon={X}
                      size="sm"
                      aria-label={t("Forget “{{query}}”", { query: entry.query })}
                      onClick={() => forget.mutate({ id: entry.id })}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : (
        <>
          {results ? (
            <Text variant="small">{t("{{count}} results", { count: results.total })}</Text>
          ) : null}
          <DocumentList
            documents={documents}
            isLoading={isFetching}
            emptyIcon={MagnifyingGlass}
            emptyMessage={t("No results for “{{query}}”", { query })}
          />
        </>
      )}
    </div>
  );
}
